'use client';

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

import type { WorksheetPhoto } from './worksheetPhoto';

const PREVIEW_WIDTH = 320;
const PREVIEW_DELAY_MS = 150;
const IMAGE_OUTLINE = 'outline outline-1 -outline-offset-1 outline-white/10';

const photoName = (photo: WorksheetPhoto) =>
  photo.label ? `Open photo: ${photo.label}` : 'Open photo';

const useCanHover = () => {
  const [canHover, setCanHover] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setCanHover(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return canHover;
};

const placePreview = (anchor: DOMRect) => {
  const estimatedHeight = 248;
  const gap = 8;
  const margin = 8;
  const left = Math.min(
    Math.max(margin, anchor.left + anchor.width / 2 - PREVIEW_WIDTH / 2),
    window.innerWidth - PREVIEW_WIDTH - margin
  );
  const above = anchor.top - estimatedHeight - gap;
  const top = above >= margin ? above : anchor.bottom + gap;
  return { top, left };
};

const usePhotoPreview = (anchorRef: RefObject<HTMLElement | null>) => {
  const timerRef = useRef<number | null>(null);
  const canHover = useCanHover();
  const [preview, setPreview] = useState<{ top: number; left: number } | null>(
    null
  );

  const clearTimer = () => {
    if (timerRef.current === null) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const hidePreview = () => {
    clearTimer();
    setPreview(null);
  };

  const showPreview = () => {
    if (!canHover) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPreview(placePreview(rect));
    }, PREVIEW_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!preview) return;
    const hide = () => setPreview(null);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [preview]);

  return { preview, showPreview, hidePreview };
};

function PhotoPreview({
  photo,
  preview,
}: {
  photo: WorksheetPhoto;
  preview: { top: number; left: number } | null;
}) {
  if (!preview) return null;

  return createPortal(
    <div
      data-cy='worksheet-photo-preview'
      aria-hidden
      className='pointer-events-none fixed z-[2000] w-80 rounded-2xl bg-card p-2 shadow-[0_8px_24px_rgb(0_0_0/0.28),0_2px_6px_rgb(0_0_0/0.18)]'
      style={{ top: preview.top, left: preview.left }}
    >
      <img
        src={photo.thumbnailUrl}
        alt=''
        className={cn(
          'max-h-60 w-full rounded-lg bg-white/[0.04] object-contain',
          IMAGE_OUTLINE
        )}
      />
      {photo.label ? (
        <p className='line-clamp-3 px-1 pt-2 text-xs leading-5 text-fg-muted'>
          {photo.label}
        </p>
      ) : null}
    </div>,
    document.body
  );
}

export function WorksheetPhotoThumb({
  photo,
  onOpen,
}: {
  photo: WorksheetPhoto;
  onOpen: () => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [failed, setFailed] = useState(false);
  const { preview, showPreview, hidePreview } = usePhotoPreview(buttonRef);

  return (
    <>
      <button
        ref={buttonRef}
        type='button'
        data-cy='worksheet-photo-thumb'
        aria-label={photoName(photo)}
        onClick={onOpen}
        onMouseEnter={() => {
          if (!failed) showPreview();
        }}
        onMouseLeave={hidePreview}
        onFocus={hidePreview}
        className='inline-flex w-24 shrink-0 flex-col gap-1 rounded-lg text-left transition-transform duration-150 ease-[cubic-bezier(0.2,0,0,1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100'
      >
        {failed ? (
          <span className='flex h-[72px] w-full items-center justify-center rounded-lg bg-white/[0.04] px-2 text-center text-[11px] leading-4 text-fg-muted ring-1 ring-inset ring-white/10'>
            Photo unavailable
          </span>
        ) : (
          <img
            src={photo.thumbnailUrl}
            alt=''
            width={96}
            height={72}
            loading='lazy'
            draggable={false}
            onError={() => {
              setFailed(true);
              hidePreview();
            }}
            className={cn(
              'h-[72px] w-full rounded-lg bg-white/[0.04] object-contain',
              IMAGE_OUTLINE
            )}
          />
        )}
        {photo.label ? (
          <span className='line-clamp-2 text-[11px] leading-4 text-fg-muted'>
            {photo.label}
          </span>
        ) : null}
      </button>
      <PhotoPreview photo={photo} preview={failed ? null : preview} />
    </>
  );
}

/** Inline photo link. Hover and click match the strip; the sentence keeps its shape. */
export function WorksheetPhotoLink({
  photo,
  onOpen,
  children,
  className,
}: {
  photo: WorksheetPhoto;
  onOpen: () => void;
  children: ReactNode;
  className?: string;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { preview, showPreview, hidePreview } = usePhotoPreview(buttonRef);

  return (
    <>
      <button
        ref={buttonRef}
        type='button'
        data-cy='worksheet-photo-link'
        aria-label={photoName(photo)}
        onClick={onOpen}
        onMouseEnter={showPreview}
        onMouseLeave={hidePreview}
        onFocus={hidePreview}
        className={cn(
          'inline cursor-pointer border-0 bg-transparent p-0 align-baseline font-[inherit] text-[length:inherit] leading-[inherit]',
          className
        )}
      >
        {children}
      </button>
      <PhotoPreview photo={photo} preview={preview} />
    </>
  );
}
